import { loader } from '@/Engine/loader';
import { V3dTools } from '@/v3d/common';
import { ShelfSet } from '@/Engine/Wardrobe/ShelfSet';
import { RackModule } from '@/Engine/Wardrobe/RackModule';
import {
    moduleItem, railGroup, Shelf, sides,
    wardrobeData,
    wardrobeModule,
    xyz,
} from '@/Interfaces/common';
import { PerforatedPanel } from '@/Engine/Wardrobe/PerforatedPanel';
import { Room } from '@/Engine/Room';
import { IV3D } from '@/Interfaces/v3d';
import { $ } from '@/Engine/state';
import { Vector3 } from 'three';
import { findRail, getModuleTemplate } from '@/lib/createModule';
import { RailGroup } from '@/Engine/Wardrobe/RailGroup';
import { wardrobeDataDebug } from '@/Constants/3dConfig';
import {  updatePartsForAllShelves } from '@/Engine/eventHandlers/lib/shelfDocking';
import { onTempItems } from '@/Engine/eventHandlers/onTempItems';
import { useUIOnlyStore } from '@/Store/storeUI';
import { markCollisions } from '@/Engine/eventHandlers/lib/markCollisions';
import { autoRodDocking } from '@/Engine/eventHandlers/lib/attachRod';

declare let v3d: IV3D;
export class Wardrobe {
    room: Room;
    baseColor = '#ff0000';
    localDataHash = 0;
    v3dTools: V3dTools;
    rackModules: Array<RackModule> = [];
    shelves: Array<Shelf> = [];
    railGroup: Array<RailGroup> = [];

    constructor(room: Room, v3dTools: V3dTools) {
        this.room = room;
        this.v3dTools = v3dTools;
    }

    deleteEmptyRailsAndRacks() {
        this.railGroup.forEach((rail)=>{
            if (!rail.racks.length)
                this.removeRailsByUUID(rail.uuid);

        });
        this.rackModules.forEach((rack)=>{
            if (!rack.shelves.length)
                this.removeRailsByUUID(rack.uuid);

        });
    }

    get raw() {
        this.deleteEmptyRailsAndRacks();
        return {
            shelves: this.shelves.map((item)=>item.raw),
            rails: this.railGroup.map((item)=>item.raw),
            modules: this.rackModules.map((item)=>item.raw),
        };
    }

    loader = loader;

    getRailByUUID = (uuid: string) => {
        return this.railGroup.find((item) => item.uuid === uuid);
    };

    getModuleByUUID = (uuid: string) => {
        return this.rackModules.find((module) => module.uuid === uuid);
    };

    getShelfByUUID = (uuid: string) => {
        return this.shelves.find((item) => item.uuid === uuid);
    };

    getShelvesByModuleUUID = (uuid: string) => {
        return this.shelves.filter((item) => item.rackModule?.uuid === uuid);
    };

    getModulesByRailUUID = (uuid: string) => {
        return this.rackModules.filter((item)=>item.railGroup?.uuid === uuid);
    };

    // moveShelvesByModule = (uuid: string, position) => {
    //     this.getShelvesByModuleUUID(uuid).forEach((shelf)=> {
    //         shelf.position = position;
    //     });
    // };
    addShelf(itemData: moduleItem) {
        const position = itemData.position ?? [ 0,0,0 ];
        const side = itemData.side;
        let item = this.getShelfByUUID(itemData.uuid);

        if (item) {
            // Если еще не добавили все полки, то модуль полки переместиться вслед за полкой
            const oldPos = item.position.clone();
            const oldSide = item.side;
            const oldPlace = item.place;
            this.room.addToWall(item.model, side);
            item.position = new Vector3(...position);
            item.place = itemData.place;
            if (item instanceof ShelfSet &&
                !!item.templates.rod !== !!itemData.templates.rod) {
                item.templates = JSON.parse(JSON.stringify(itemData.templates));
                item.updateModels(item.templates);
                item.updatePartPosition();
            }
            // if ($.checkCollision(item, side)) {
            //     this.room.addToWall(item.model, oldSide);
            //     item.position = oldPos;
            //     item.place = oldPlace;
            // } else {
            //     // console.log('No collision');
            // }
            item.templates = JSON.parse(JSON.stringify(itemData.templates));

            return item;
        }
        if (itemData.type === 'shelf')
            item = new ShelfSet({
                v3dTools: this.v3dTools,
                ...itemData,
                parent: this,
            });

        if (itemData.type === 'panel')
            item = new PerforatedPanel({
                v3dTools: this.v3dTools,
                ...itemData,
                parent: this,
            });

        if (!item) {
            console.error('undefined type ' + itemData.type);
            return;
        }
        this.room.addToWall(item.model, side);
        item.position = new Vector3(...position);
        this.shelves.push(item);
        // if (item.model)
        return item;
    }

    removeTempRackModules() {
        let i = this.rackModules.length;
        while (i--) {
            if (this.rackModules[i].uuid === 'temp') {
                this.rackModules[i].model.removeFromParent();
                // this.rackModules[i].railModel.removeFromParent();
                this.rackModules.splice(i, 1);
            }
        }
    }

    removeTempRailGroup() {
        let i = this.railGroup.length;
        while (i--) {
            if (this.railGroup[i].uuid === 'temp') {
                this.railGroup[i].rails.forEach((rail)=>{
                    rail.model.removeFromParent();
                });
                this.railGroup[i].model.removeFromParent();
                this.railGroup.splice(i, 1);
            }
        }
    }

    addModuleForShelf(shelfData: moduleItem, rackModules: Array<wardrobeModule>, shelf): [wardrobeModule, RackModule] {
        let rackModuleTemplate = null;
        let rackModule = null;

        if (shelfData.rackModuleUUID) {
            rackModuleTemplate = rackModules.find((module) => module.uuid === shelfData.rackModuleUUID);
            if (!rackModuleTemplate)
                console.warn('У полки нет привязанной рейки');
        }

        if (!rackModuleTemplate) {
            rackModuleTemplate = getModuleTemplate(shelfData);
        }
        rackModule = this.addModule(rackModuleTemplate, shelfData.side);
        rackModule.position = shelf.position;
        shelf.rackModule = rackModule;
        shelf.updateRackHeight();

        return [ rackModuleTemplate, rackModule ];
    }

    addRailForRack(rackTemplate: wardrobeModule, railsData: Array<railGroup>, rack: RackModule): railGroup {
        this.removeTempRailGroup();
        const railItem = findRail(rack.compatibilityWidth || rackTemplate.width,
            rackTemplate.color || 'ffffff');
        // Создаем шаблон рельса
        let railGroupTemplate: railGroup = {
            ...railItem,
            uuid: rackTemplate.railGroupUUID ?? crypto.randomUUID(),
            templates: [ railItem ],
            position: rack.position.toArray(),
            side: rack.side,
        };
        // Если у рейки есть привязанный рельс, то пробуем найти его шаблон в данных из стора
        if (rackTemplate.railGroupUUID) {
            const existRailGroup = railsData.find((module) =>
                module.uuid === rackTemplate.railGroupUUID);
            if (existRailGroup)
                railGroupTemplate = existRailGroup;
            else
                console.warn('У рейки нет привязанного рельса');
        }

        // Ищем готовый рельс
        let newRailGroup = this.railGroup.find((item)=>item.uuid === railGroupTemplate.uuid);
        // Если нет готового, создаем новый
        if (!newRailGroup) {
            newRailGroup = new RailGroup({
                ...railGroupTemplate,
                parent: this,
                v3dTools: this.v3dTools,
            });
            this.railGroup.push(newRailGroup);
            // $.tempRail = railGroupTemplate;
        }
        // Привязываем стойку к рельсу
        rack.railGroup = newRailGroup;
        newRailGroup.position = rack.position;
        $.room.addToWall(newRailGroup.model, rack.side);
        newRailGroup.update();
        return railGroupTemplate;
    }

    addModule(moduleData: wardrobeModule, side?: sides) {
        this.removeTempRackModules();
        let module = this.getModuleByUUID(moduleData.uuid) as RackModule;
        if (!module) {
            module = new RackModule({
                v3dTools: this.v3dTools,
                side,
                ...moduleData,
                parent: this,
                //     onChangePosition: ((uuid, position)=>{
                //         useConfigurationStore().setPositionByUUID(uuid, position);
                // }),
            });
            // this.addModuleChildren(module, moduleData.children);
            this.rackModules.push(module);
        }

        module.position = new v3d.Vector3(...moduleData.position as xyz);
        // module.removeTempChild();
        // if (module.children.length !== moduleData.children.length || moduleData.needUpdate) {
        // module.removeChildren();
        // this.addModuleChildren(module, moduleData.children);
        // }
        this.room.addToWall(module.model, moduleData.side);
        // this.room.addToWall(module.railModel, moduleData.side);
        module.computeObb();
        return module;
    }

    removeShelfByUUID(uuid: string) {
        let i = this.shelves.length;
        while (i--) {
            if (uuid === this.shelves[i].uuid) {
                this.shelves[i].model.removeFromParent();
                if (this.shelves[i].rackModule &&
                    this.shelves[i].rackModule.shelves.length === 1)
                        this.removeModuleByUUID(this.shelves[i].rackModule.uuid);
                this.shelves.splice(i, 1);
            }
        }
    }
    removeModuleByUUID(uuid: string) {
        let i = this.rackModules.length;
        while (i--) {
            if (uuid === this.rackModules[i].uuid) {
                this.rackModules[i].model.removeFromParent();
                if (this.rackModules[i].railGroup &&
                    this.rackModules[i].railGroup.racks.length === 1)
                        this.removeRailsByUUID(this.rackModules[i].railGroup.uuid);
                this.rackModules.splice(i, 1);
            }
        }
    }

    removeRailsByUUID(uuid: string) {
        let i = this.railGroup.length;
        while (i--) {
            if (uuid === this.railGroup[i].uuid) {
                this.railGroup[i].model.removeFromParent();
                this.railGroup.splice(i, 1);
            }
        }
    }

    // removeTempChildren() {
    //     this.rackModules.forEach((module, index)=>{
    //         module.removeTempChild();
    //     });
    // }

    removeNotIncludedRack(uuids: Array<string>) {
        let i = this.rackModules.length;
        while (i--) {
            if (!uuids.includes(this.rackModules[i].uuid)) {
                this.rackModules[i].model.removeFromParent();
                this.rackModules.splice(i, 1);
            }
        }
    }

    removeNotIncludedRails(uuids: Array<string>) {
        let i = this.railGroup.length;
        while (i--) {
            if (!uuids.includes(this.railGroup[i].uuid)) {
                // this.rackModules[i].removeChildren();
                this.railGroup[i].model.removeFromParent();
                // this.rackModules[i].railModel.removeFromParent();
                this.railGroup.splice(i, 1);
            }
        }
    }

    add(data: wardrobeData) {
        const newUUIDs: string[] = [];
        data.shelves.forEach((shelf)=>{
            shelf.uuid = crypto.randomUUID();
            const uuid = shelf.rackModuleUUID;
            if (!uuid) return;
            if (newUUIDs.includes(uuid)) return;
            shelf.rackModuleUUID = crypto.randomUUID();
            newUUIDs.push(shelf.rackModuleUUID);
            data.shelves
                .filter((shelf2)=>shelf2.rackModuleUUID === uuid)
                .forEach((shelf2)=>shelf2.rackModuleUUID = shelf.rackModuleUUID);
            data.modules
                .filter((module)=>module.uuid === uuid)
                .forEach((module)=>module.uuid = shelf.rackModuleUUID);
        });

        data.modules.forEach((module)=>{
            const uuid = module.railGroupUUID;
            if (!uuid) return;
            if (newUUIDs.includes(uuid)) return;
            module.railGroupUUID = crypto.randomUUID();
            newUUIDs.push(module.railGroupUUID);
            data.modules
                .filter((module2)=>module2.railGroupUUID === uuid)
                .forEach((module2)=>module2.railGroupUUID=module.railGroupUUID);
            data.rails
                .filter((rail)=>rail.uuid === uuid)
                .forEach((rail)=>rail.uuid=module.railGroupUUID);
        });

        this.update({
            shelves: [ ...this.shelves.map((item)=>item.raw), ...data.shelves ],
            rails: [ ...this.railGroup.map((item)=>item.raw), ...data.rails ],
            modules: [ ...this.rackModules.map((item)=>item.raw), ...data.modules ],
            doors: [],
            windows: [],
        });
    }

    update(data: wardrobeData) {
        if (wardrobeDataDebug) {
            console.log('_______________________');
            console.log('Полок: ', data.shelves.length);
            console.log('Модулей: ', data.modules.length);
            console.log('Рельс: ', data.rails.length);
            data.shelves.forEach((shelf) => {
                if (!shelf.rackModuleUUID || shelf.rackModuleUUID === 'temp')
                    console.warn('У полки не задан модуль');
            });

            data.rails.forEach((rail, index) => {
                const rackUUIDs = data.modules.filter((rack) => rack.railGroupUUID === rail.uuid);
                console.log('У рельса ' + index + ': модулей ' + rackUUIDs.length);
            });

            data.modules.forEach((rack, index) => {
                const shelves = data.shelves.filter((shelf) => shelf.rackModuleUUID === rack.uuid);
                console.log('У модуля ' + index + ': полок ' + shelves.length);
                if (!rack.railGroupUUID || rack.railGroupUUID === 'temp')
                    console.warn('У модуля не задан рельс');
            });
        }

        const oldState = JSON.parse(JSON.stringify($.wardrobe.raw));
        $.app.scene.traverse((object)=>{
            if (object.userData.type === 'rack') {
                if (!this.getModuleByUUID(object.userData.uuid)) {
                    console.warn('Удаление мусорной стойки');
                    object.removeFromParent();
                }
            }
        });
        // TODO
        // this.rackModules.forEach((module)=>{
        //     module.model.removeFromParent();
        // });
        // this.rackModules = [];
        // this.app.scene.add(part.model);
        //Удаляем со сцены полки которых нет в сторе
        let i = this.shelves.length;
        const shelfUUIDs = data.shelves.map((shelf: moduleItem)=>shelf.uuid);
        while (i--) {
            if (!shelfUUIDs.includes(this.shelves[i].uuid)) {
                this.shelves[i].model.removeFromParent();
                this.shelves.splice(i, 1);
            }
        }

        const modulesUUID = data.modules.map((module)=>module.uuid);
        this.removeNotIncludedRack(modulesUUID);

        const railsUUID = data.rails.map((rail)=>rail.uuid);
        this.removeNotIncludedRails(railsUUID);
        // Добавляем или обновляем полку
        data.shelves.forEach((shelfData)=>{
            const shelf = this.addShelf(shelfData);
            if (shelf && shelfData.position) shelf.position.set(...shelfData.position);
            const [ rackTemplate, rack ] = this.addModuleForShelf(shelfData, data.modules, shelf);
            const railGroupTemplate = this.addRailForRack(rackTemplate, data.rails, rack);
            if (shelf && shelfData.place) shelf.place = shelfData.place;

        });
        // returnBracketsForAllShelves();
        if (autoRodDocking()) {
            // onTempItems();
            // return;
        }

        updatePartsForAllShelves();

        // Проверяем есть ли изменения в конфигурации
        const newState = JSON.parse(JSON.stringify($.wardrobe.raw));
        let updateFlag = false;

        if (newState.modules.length !== oldState.modules.length) updateFlag = true;
        if (newState.shelves.length !== oldState.shelves.length) updateFlag = true;
        if (newState.rails.length !== oldState.rails.length) updateFlag = true;
        newState.shelves.forEach((shelf: Shelf, index: number)=>{
            if (!shelf) return;
            if (!oldState.shelves[index]) return;

            if (shelf.templates.shelfBracketLeft?.disabled !== oldState.shelves[index].templates.shelfBracketLeft?.disabled)
                updateFlag = true;

            if (shelf.templates.shelfBracketRight?.disabled !== oldState.shelves[index].templates.shelfBracketRight?.disabled)
                updateFlag = true;

            if (shelf.templates.assets.length !== oldState.shelves[index].templates.assets.length)
                updateFlag = true;
        });
        newState.modules.forEach((module: RackModule, index: number)=>{
            if (!module) return;
            if (!oldState.modules[index]) return;

            if (module.templates[1]?.disabled !== oldState.modules[index].templates[1]?.disabled)
                updateFlag = true;

            if (module.templates[2]?.disabled !== oldState.modules[index].templates[2]?.disabled)
                updateFlag = true;

        });

        if (updateFlag)
            onTempItems();

        const allUUIds = data.shelves.map((item)=>item.uuid);
        allUUIds.push(...data.modules.map((item)=>item.uuid));
        if ($.selectionBox.target &&
            !allUUIds.includes($.selectionBox.target.uuid) &&
            ![ 'window','door' ].includes($.selectionBox.target.type)
        ) {
            $.selectionBox.unselect();
            $.selectionBoxForBox.unselect();
        }

        if ($.selectionBox.targetGroup[0]) {
            if ($.selectionBox.targetGroup[0].railGroup?.racks)
                $.selectionBox.selectGroup($.selectionBox.targetGroup[0].railGroup.racks);
            else {
                $.selectionBox.unselect();
                $.selectionBoxForBox.unselect();
            }

        }

        // const newHash = hashCode(JSON.stringify(data))

        if (useUIOnlyStore().deleteFlag) {
            useUIOnlyStore().setDeleteFlag(false);
            onTempItems();
        }
        // this.localDataHash = newHash;


    }



    // unselectAll() {
    //     this.rackModules.forEach((module)=>{
    //         module.unselect();
    //     });
    // }
    _setColor(hex: string) {
        this.baseColor = hex.startsWith('#') ? hex : '#' + hex;

        // this.rackModules[0]?.setColor(hex);
        this.rackModules.forEach((module) => {
            module.setColor(this.baseColor);
        });
        this.railGroup.forEach((item) => {
            item.setColor(this.baseColor);
        });
        this.shelves.forEach((item) => {
            item.setColor(this.baseColor);
        });
    }

    setColor(hex: string) {
        this._setColor(hex);
        // Меняем цвет, где коллизия
        markCollisions();
    }

    addDecor() {
        this.shelves.forEach((shelf)=>{
            shelf.addDecor();
        });
    }

    removeDecor() {
        this.shelves.forEach((shelf)=>{
                shelf.clearDecor();
            });
    }

}
